/**
 * Path A — Amazon Lex V2 via a small Lambda proxy.
 *
 * The browser POSTs the user's typed/spoken text to /api/lex/recognize-text,
 * which is a CloudFront behavior pointing at our Lex proxy Lambda. The Lambda
 * calls `lexv2-runtime.recognize_text` against the deployed voicedemo-bank-bot
 * and returns the bot's reply.
 *
 * Voice on Path A is "narrated by Web Speech API" for the demo — the browser
 * synthesizes the response locally so visitors hear the bot speak. The real
 * Polly Joanna voice is available on the phone version (Phase 6 / Connect).
 */
import { AdapterHandlers, Message, PathAdapter } from "../types";

const API_PATH = "/api/lex/recognize-text";

export class LexPathAdapter implements PathAdapter {
  private sessionId = `web-${Math.random().toString(36).slice(2, 10)}`;
  private handlers: AdapterHandlers | null = null;

  async start(handlers: AdapterHandlers): Promise<void> {
    this.handlers = handlers;
    handlers.onStatus("Connected to Lex. Type or speak to begin.");
  }

  async sendText(text: string): Promise<void> {
    if (!this.handlers) throw new Error("LexPathAdapter not started");
    const h = this.handlers;
    const userMsg: Message = { id: msgId(), role: "user", content: text, ts: Date.now() };
    h.onMessage(userMsg);

    const started = performance.now();
    try {
      const resp = await fetch(API_PATH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, sessionId: this.sessionId }),
      });
      if (!resp.ok) throw new Error(`lex proxy: HTTP ${resp.status}`);
      const data: LexProxyResponse = await resp.json();
      const latency = Math.round(performance.now() - started);

      for (const m of data.messages ?? []) {
        h.onMessage({ id: msgId(), role: "bot", content: m, ts: Date.now() });
        speak(m);
      }

      h.onMetric({
        latencyMs: latency,
        toolCalls: data.intent && data.intent !== "FallbackIntent" ? 1 : 0,
        fallbacks: data.intent === "FallbackIntent" ? 1 : 0,
      });
      h.onStatus(`Lex → ${data.intent ?? "?"} (${data.state ?? "?"})`);
    } catch (err) {
      h.onError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  async stop(): Promise<void> {
    cancelSpeech();
    this.handlers = null;
  }
}

interface LexProxyResponse {
  intent?: string;
  state?: string;
  messages?: string[];
  slots?: Record<string, string | null>;
}

function msgId(): string {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

// --- Web Speech API helpers (browser-native TTS for Path A demo) ---------- //
function speak(text: string): void {
  if (!("speechSynthesis" in window)) return;
  cancelSpeech();
  const utt = new SpeechSynthesisUtterance(text);
  utt.rate = 1.0;
  utt.pitch = 1.0;
  // Try to pick a female en-US voice to roughly match Polly Joanna.
  const voices = window.speechSynthesis.getVoices();
  const preferred =
    voices.find((v) => /joanna|samantha|jenny|aria|emma/i.test(v.name) && v.lang.startsWith("en")) ||
    voices.find((v) => v.lang.startsWith("en-US"));
  if (preferred) utt.voice = preferred;
  window.speechSynthesis.speak(utt);
}

function cancelSpeech(): void {
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
}
